import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Dock, type DockInput, type DockDistance } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column } from '@/components/ui';
import { IconMap } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Docks — the named pickup/drop points a Telride Boats charter runs between
 *  (migration 056). A dock is a name + centre (lat/lng) + address; the customer
 *  Boats screen lists the active docks and the fare is $12/km against the
 *  per-pair dock-distance matrix below the table.
 *
 *  AUTHORED here by staff with docks.manage (ops + superadmin): add / edit /
 *  deactivate a dock + set the per-pair distances (admin-svc owns the tables
 *  directly, like booking-statuses; audited + RBAC-gated). Read-only roles still
 *  see the list. DARK: Telride Boats launches Sept 2026 — nothing consumes this
 *  data until then. DEFENSIVE: shows a "not yet available" state until migration
 *  056 lands. */
export function Docks() {
  const [editing, setEditing] = useState<Dock | 'new' | null>(null);
  const canManage = useAdminPermission('docks.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'docks'],
    queryFn: ({ signal }) => adminApi.docks(signal),
  });

  const unavailable = data && data.available === false;
  const docks = data?.items ?? [];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Docks"
        subtitle="Telride Boats pickup/drop points + the per-pair billable distances. Dark until the Sept 2026 launch — nothing consumes this yet."
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Docks not yet available"
            description={data?.note ?? 'The docks tables (migration 056) are not in this Postgres yet. This view lights up once applied.'}
            icon={<IconMap className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {canManage ? (
            <div className="mb-3 flex justify-end">
              <button type="button" onClick={() => setEditing('new')} className="btn-primary">
                New dock
              </button>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading docks…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load docks" description={(error as Error)?.message} />
            ) : (
              <DocksTable rows={docks} canManage={canManage} onEdit={(d) => setEditing(d)} />
            )}
          </Card>

          {/* The per-pair distance matrix (the fare basis). Only meaningful with
              ≥2 docks. The lookup is symmetric, so only the upper triangle is
              editable. */}
          {docks.length >= 2 ? (
            <div className="mt-6">
              <div className="mb-2 text-sm font-semibold text-slate-200">Dock-to-dock distances (km)</div>
              <Card flush>
                <DockDistanceMatrix docks={docks} canManage={canManage} />
              </Card>
            </div>
          ) : null}
        </>
      )}

      {editing ? <DockDrawer dock={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </AdminPage>
  );
}

/** The docks DataTable — id / name / centre / status + (for docks.manage) an
 *  inline activate-deactivate toggle and an Edit button. There is NO hard delete
 *  in v1 (deactivating keeps the dock-distance rows intact). */
function DocksTable({ rows, canManage, onEdit }: { rows: Dock[]; canManage: boolean; onEdit: (d: Dock) => void }) {
  const qc = useQueryClient();
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const toggle = useMutation({
    mutationFn: ({ dock, active }: { dock: Dock; active: boolean }) =>
      adminApi.updateDock(dock.id, {
        name: dock.name,
        lat: dock.lat,
        lng: dock.lng,
        address: dock.address,
        is_active: active,
      }),
    onMutate: ({ dock }) => setTogglingId(dock.id),
    onSettled: () => {
      setTogglingId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'docks'] });
    },
  });

  const columns: Array<Column<Dock>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-slate-400">#{r.id}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{r.name}</span> },
    {
      key: 'centre',
      header: 'Centre',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
        </span>
      ),
    },
    { key: 'address', header: 'Address', cell: (r) => <span className="text-xs text-slate-300">{r.address || '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.is_active ? 'ok' : 'neutral'} dot>
          {r.is_active ? 'active' : 'inactive'}
        </Badge>
      ),
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={toggle.isPending && togglingId === r.id}
            onClick={() => toggle.mutate({ dock: r, active: !r.is_active })}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-ink-800 disabled:opacity-50"
          >
            {toggle.isPending && togglingId === r.id ? '…' : r.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            type="button"
            onClick={() => onEdit(r)}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
          >
            Edit
          </button>
        </div>
      ),
    });
  }

  return (
    <DataTable<Dock>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={<EmptyState title="No docks" description="No docks yet — add one to start building the Boats network." icon={<IconMap className="h-6 w-6" />} />}
    />
  );
}

/** DockDistanceMatrix — an N×N grid of the billable km between docks. The lookup
 *  is symmetric ((a,b) falls back to (b,a)), so only the UPPER triangle is
 *  editable; the diagonal is disabled and the lower triangle mirrors read-only.
 *  Each upper-triangle input saves on blur. */
function DockDistanceMatrix({ docks, canManage }: { docks: Dock[]; canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'dock-distances'],
    queryFn: ({ signal }) => adminApi.dockDistances(signal),
  });

  const save = useMutation({
    mutationFn: ({ from, to, km }: { from: number; to: number; km: number }) => adminApi.setDockDistance(from, to, km),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'dock-distances'] }),
  });

  // Local edit buffer keyed `${from}-${to}` (the canonical upper-triangle pair).
  const [buf, setBuf] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Loading distances…" />
      </div>
    );
  }

  const dists: DockDistance[] = data?.items ?? [];
  const lookup = (a: number, b: number): number | null => {
    const hit = dists.find((d) => (d.from_dock_id === a && d.to_dock_id === b) || (d.from_dock_id === b && d.to_dock_id === a));
    return hit ? hit.distance_km : null;
  };
  const cellValue = (a: number, b: number): string => {
    const key = `${a}-${b}`;
    if (key in buf) return buf[key];
    const v = lookup(a, b);
    return v === null ? '' : String(v);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 bg-ink-900 p-2 text-left text-muted">from \ to</th>
            {docks.map((d) => (
              <th key={d.id} className="whitespace-nowrap p-2 text-left text-muted">
                {d.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {docks.map((rowD, i) => (
            <tr key={rowD.id} className="border-t border-ink-800">
              <td className="sticky left-0 whitespace-nowrap bg-ink-900 p-2 font-medium text-slate-200">{rowD.name}</td>
              {docks.map((colD, j) => {
                if (i === j) {
                  return (
                    <td key={colD.id} className="p-2 text-center text-muted">
                      —
                    </td>
                  );
                }
                if (i > j) {
                  // Lower triangle: read-only mirror of the symmetric value.
                  const mirror = lookup(rowD.id, colD.id);
                  return (
                    <td key={colD.id} className="p-2 text-center text-slate-500">
                      {mirror === null ? '·' : mirror}
                    </td>
                  );
                }
                // Upper triangle: editable.
                const key = `${rowD.id}-${colD.id}`;
                return (
                  <td key={colD.id} className="p-1.5">
                    <input
                      type="number"
                      disabled={!canManage || save.isPending}
                      value={cellValue(rowD.id, colD.id)}
                      onChange={(e) => setBuf((b) => ({ ...b, [key]: e.target.value }))}
                      onBlur={(e) => {
                        const km = Number(e.target.value);
                        if (e.target.value.trim() === '' || !Number.isFinite(km) || km < 0) return;
                        if (km === lookup(rowD.id, colD.id)) return; // unchanged
                        save.mutate({ from: rowD.id, to: colD.id, km });
                      }}
                      className="input w-20 px-1.5 py-1 text-center text-xs"
                      placeholder="—"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {save.isError ? <p className="p-2 text-xs text-danger">{(save.error as Error)?.message}</p> : null}
    </div>
  );
}

// ─── dock authoring drawer ───────────────────────────────────

interface FormState {
  name: string;
  is_active: boolean;
  lat: string;
  lng: string;
  address: string;
}

/** DockDrawer — the create/edit form. dock=null ⇒ create; otherwise edit. No
 *  delete (v1 deactivates via the Status toggle to keep distance rows intact). */
function DockDrawer({ dock, onClose }: { dock: Dock | null; onClose: () => void }) {
  const isEdit = dock !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(dock));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: DockInput) => (isEdit ? adminApi.updateDock(dock!.id, input) : adminApi.createDock(input)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'docks'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  return (
    <Drawer
      title={isEdit ? `Edit dock #${dock!.id}` : 'New dock'}
      subtitle={isEdit ? dock!.name : 'Define a Boats pickup/drop point'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="ID" value={`#${dock!.id}`} />
            <Field label="Status" value={dock!.is_active ? 'Active' : 'Inactive'} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            <TextField label="Name" hint="The dock name riders see (e.g. Circular Quay)." value={form.name} onChange={(v) => set('name', v)} placeholder="Circular Quay" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Latitude" hint="-90 to 90" value={form.lat} onChange={(v) => set('lat', v)} type="number" placeholder="-33.8610" />
              <TextField label="Longitude" hint="-180 to 180" value={form.lng} onChange={(v) => set('lng', v)} type="number" placeholder="151.2106" />
            </div>
            <TextField label="Address" hint="Optional — shown to riders." value={form.address} onChange={(v) => set('address', v)} placeholder="Circular Quay, Sydney NSW" />
            <label className="flex items-center gap-2 pt-1 text-sm text-slate-200">
              <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} className="h-4 w-4 rounded border-ink-600 bg-ink-900" />
              Active (listed on the customer Boats screen once Boats launches)
            </label>
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={mutation.isPending} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create dock'}
          </button>
          <button type="button" disabled={mutation.isPending} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 input" />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

function seedForm(d: Dock | null): FormState {
  if (!d) return { name: '', is_active: true, lat: '', lng: '', address: '' };
  return { name: d.name, is_active: d.is_active, lat: String(d.lat), lng: String(d.lng), address: d.address };
}

/** Validate + convert the form into a DockInput, or return an error string. */
function buildInput(f: FormState): { input: DockInput } | { error: string } {
  const name = f.name.trim();
  if (!name) return { error: 'Name is required.' };
  const lat = Number(f.lat.trim());
  const lng = Number(f.lng.trim());
  if (f.lat.trim() === '' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
    return { error: 'Latitude must be a number between -90 and 90.' };
  }
  if (f.lng.trim() === '' || !Number.isFinite(lng) || lng < -180 || lng > 180) {
    return { error: 'Longitude must be a number between -180 and 180.' };
  }
  return { input: { name, is_active: f.is_active, lat, lng, address: f.address.trim() } };
}
